import { useCallback, useState } from 'react';
import { isValidPayerEmail } from '@/components/payments/PayerEmailField';
import { useAuth } from '@/contexts/customer-auth-context';
import { useDebtData } from '@/contexts/DebtDataContext';
import { ensureCustomerEmailOnConnect } from '@/lib/connect-api';

/**
 * Payer-email capture state for checkout flows: whether to prompt (customer has no
 * usable email on file), the current input, and whether the requirement is satisfied.
 *
 * `emailValid` is the submit gate — true when no email is needed, or when the entered
 * one passes validation. Mirror of `isValidAmount`: block the CTA on `!emailValid`.
 *
 * `persistPayerEmail` writes the entered address via Connect *before* the checkout call,
 * so the server resolves it from file (Core never receives the raw email). It is a no-op
 * when no email is needed. Call it inside the submit handler ahead of createPaymentSession.
 */
export function usePayerEmail() {
  const { account } = useDebtData();
  const { authStage, getAccessToken } = useAuth();
  const [payerEmail, setPayerEmail] = useState('');
  // `account` is null while loading — only prompt once we know there is no usable email.
  const needsEmail = account != null && !account.customer_email;
  const emailValid = !needsEmail || isValidPayerEmail(payerEmail);

  const persistPayerEmail = useCallback(async () => {
    if (!needsEmail || !account) return;
    await ensureCustomerEmailOnConnect(account.id, payerEmail.trim(), authStage, getAccessToken);
  }, [needsEmail, account, payerEmail, authStage, getAccessToken]);

  return { needsEmail, payerEmail, setPayerEmail, emailValid, persistPayerEmail };
}
